import {ColorPicker} from '@wix/wix-ui-tpa/cssVars';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {VIEW_MORE_OPTIONS_URL_FRAGMENT} from '@wix/wixstores-client-storefront-sdk/dist/src/constants';
import classNames from 'classnames';
import React, {ComponentProps} from 'react';
import {UserInputType} from '../../../constants';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {SPECS} from '../../../specs';
import {scrollAndFocusUserInput} from '../utils/scrollAndFocusUserInput';
import {classes as colorOptionStyles} from './ColorOption.st.css';
import s from './ProductColor.scss';

export interface ProductColorsProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  optionIndex: number;
  colorPickerCounter?: number;
  id?: string;
}

export enum ProductColorsDataHook {
  Container = 'product-options-color-container',
  ColorOption = 'product-options-color',
  ColorOptionItem = 'product-options-color-item',
  ColorOptionTitle = 'product-colors-title',
}

class ProductColorsComponent extends React.Component<ProductColorsProps> {
  private readonly colorPickerContainerRef = React.createRef<HTMLDivElement>();

  public componentDidUpdate(prevProps: ProductColorsProps) {
    const {
      optionIndex,
      globals: {scrollAndFocusInputTarget, isQuickView},
    } = this.props;

    if (prevProps.globals.scrollAndFocusInputTarget !== scrollAndFocusInputTarget) {
      const isScrollAndFocusInputTarget =
        UserInputType.Selection === scrollAndFocusInputTarget?.inputType &&
        optionIndex === scrollAndFocusInputTarget?.index;

      if (isScrollAndFocusInputTarget && this.colorPickerContainerRef.current) {
        scrollAndFocusUserInput(UserInputType.Selection, this.colorPickerContainerRef.current, isQuickView);
      }
    }
  }

  public static defaultProps = {
    allowMultiple: false,
    selected: [],
  };

  private readonly handleChange: ComponentProps<typeof ColorPicker>['onChange'] = (event, itemId) => {
    const {
      optionIndex,
      globals: {handleUserInput, validateUserInputsChanges, userInputs},
    } = this.props;

    const nextSelection = this.productOption.selections.find((selection) => selection.description === itemId);

    const isDeselected =
      nextSelection && userInputs[UserInputType.Selection].some((selection) => selection?.id === nextSelection.id);

    handleUserInput(UserInputType.Selection, isDeselected ? undefined : nextSelection, optionIndex);

    validateUserInputsChanges();
  };

  private get activeSelection() {
    const {
      optionIndex,
      globals: {userInputs},
    } = this.props;

    return userInputs[UserInputType.Selection][optionIndex] ?? undefined;
  }

  private get productOption() {
    const {
      optionIndex,
      globals: {product},
    } = this.props;

    return product.options[optionIndex];
  }

  private get colorPickerOptions(): JSX.Element[] {
    const {
      globals: {variantInfo, isBackInStockEnabled},
    } = this.props;

    const getSelectionAvailabilityInfo = (selectionId: number) =>
      variantInfo.selectionsAvailability[this.productOption.id][selectionId];

    return this.productOption.selections
      .filter((selection) => getSelectionAvailabilityInfo(selection.id).isVisible)
      .map((selection) => {
        const isDisabled = !getSelectionAvailabilityInfo(selection.id).isSelectable;
        return (
          <ColorPicker.Item
            key={selection.id}
            id={selection.description}
            aria-label={selection.description}
            value={selection.value}
            disabled={isBackInStockEnabled ? false : isDisabled}
            isCrossedOut={isDisabled}
            tooltip={selection.description}
            checked={(this.activeSelection?.id ? [this.activeSelection.id] : []).includes(selection.id)}
            data-hook={ProductColorsDataHook.ColorOptionItem}
            className={s.colorOptions}
          />
        );
      });
  }

  public readonly renderDescription = () => (this.activeSelection ? `: ${this.activeSelection.description}` : '');

  private renderColorPicker() {
    const {
      optionIndex,
      globals: {isProductSubmitted, userInputErrors, isQuickView},
      t,
    } = this.props;

    const hasError = isProductSubmitted && userInputErrors[UserInputType.Selection][optionIndex];
    const errorMessage = hasError ? t('SELECT_OPTION_WARNING', {optionTitle: this.productOption.title}) : undefined;

    return (
      <ColorPicker
        className={classNames(
          colorOptionStyles.colorOption,
          {[s.productOptionsColorContainer]: isQuickView},
          {[s.title]: true}
        )}
        label={`${this.productOption.title}${this.renderDescription()}`}
        onChange={this.handleChange}
        data-hook={ProductColorsDataHook.ColorOption}
        errorMessage={errorMessage}
        required
        showAsterisk>
        {this.colorPickerOptions}
      </ColorPicker>
    );
  }

  public render(): JSX.Element {
    const {colorPickerCounter} = this.props;
    const {useExperiments} = this.props.globals;
    const id = useExperiments.enabled(SPECS.StickyAddToCartMobile)
      ? this.props.id
      : `${VIEW_MORE_OPTIONS_URL_FRAGMENT}-${colorPickerCounter}`;

    const selectionIds = this.productOption.selections.map((selection) => selection.id).join('_');
    const titleId = `product-colors-title-${selectionIds}`;

    return (
      <div
        id={id}
        data-hook={ProductColorsDataHook.Container}
        aria-labelledby={titleId}
        className={s.uiTpaColoPicker}
        ref={this.colorPickerContainerRef}>
        <div className={s.actionable}>{this.renderColorPicker()}</div>
      </div>
    );
  }
}

export const ProductColors: React.FC<ProductColorsProps & ProvidedGlobalProps & IProvidedTranslationProps> =
  withGlobalProps(withTranslations('globals.texts')(ProductColorsComponent));
